// IMPORT/LIBRERIE
import { useState, useEffect } from "react";
import { I18nextProvider, useTranslation } from "react-i18next";
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from "react-router-dom";

// CONFIGURAZIONI/COSTANTI
import { RECAPTCHA_SITE_KEY, AGENCY_ID, IUBENDA_SITE_ID, DEFAULT_AGENCY_NAME, appConfig } from "./config/appConfig";
import i18n from "./config/i18n/i18n";

// COMPONENTI
import NotFound from "./components/Error/NotFound";
import HomePage from "./components/Home/Home";
import AboutUs from "./components/AboutUs/AboutUs";
import Contacts from "./components/Contacts/Contacts";
import Header from "./components/Header/Header";
import Footer from "./components/Footer/Footer";
import Immobili from "./components/Properties/Immobili";
import ImmobiliSuccess from "./components/Properties/ImmobiliSuccess";
import PropertySold from "./components/Property/PropertySold";
import Sell from "./components/Static/Vendi";
import Blog from "./components/Blog/Blog";
import BlogDetail from "./components/Blog/BlogDetail";
import Dati from "./components/Policies/Routes/Dati";
import PropertyDetail from "./components/Property/Property";
import IubendaButton from "./components/Iubenda/IubendaButton";
import Loader from "./components/Loaders/Loader";
import LanguageSelector from "./components/Language/LanguageSelector";
import LanguageRouteHandler from "./components/Language/LanguageRouteHandler";
import RecaptchaWrapper from "./components/Wrappers/RecaptchaWrapper";
import PolicyPageRoute from './components/Policies/PolicyPageRoute';
import { getPolicyIdForLang } from "./utils/getPolicyIdForLang";
import WhatsAppButton from "./components/Widgets/WhatsappWidget";
import HolidayPopup from "./components/Popups/HolidayPopup";
import Services from "./components/Services/Services";
import InternalArchitect from "./components/InternalArchitect.tsx/internalArchitect";
//import Partners from "./components/Partners/Partners";
//import SellHome from "./components/VendereCasa/VendereCasa";
import Buy from "./components/Static/Cerca";

// HOOK
import { useGetAgencyDetails, Agency } from "./hooks/useGetAgencyDetails";

// FOGLI DI STILE
import "bootstrap/dist/css/bootstrap.min.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import '@flaticon/flaticon-uicons/css/all/all.css';
import './index.css'
import ScrollToTop from "./utils/scrollToTop";
import PropertyPromotion from "./components/PropertryPromotion/PropertyPromotion";

interface AppProps {
    agencyData: Agency | null;
}

const maintenance = false;

const App: React.FC<AppProps> = ({ agencyData }) => {
    const [showAgents] = useState(false);

    const { i18n: i18nInstance } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const langPrefix = appConfig.integrations.multilang ? "/:lng" : "";

    const handleLanguageChange = (lang: string) => {
        i18nInstance.changeLanguage(lang).then(() => {
            if (appConfig.integrations.multilang) {
                const currentPath = location.pathname;
                const newPath = currentPath.replace(/^\/[a-z]{2}(?=\/|$)/, `/${lang}`);
                if (!/^\/[a-z]{2}/.test(currentPath)) {
                    navigate(`/${lang}${currentPath}`);
                } else {
                    navigate(newPath, { replace: true });
                }
            }
        });
    };

    const headerAgencyData = {
        agency_name: agencyData?.name ?? appConfig.agency.defaults.name,
        address: `${agencyData?.address_details.address} - ${agencyData?.address_details.city_name} (${agencyData?.address_details.sigla}) ` || appConfig.agency.customAddress,
        phone1: agencyData?.phone1 ?? appConfig.agency.defaults.phone1,
        mobile: agencyData?.mobile ?? appConfig.agency.defaults.mobile,
        mobile2: agencyData?.mobile2 ?? "",
        mobile_visible: true,
        international_prefix: appConfig.agency.defaults.intlPrefix,
        whatsapp: false,
        facebook: false,
        instagram: true,
        twitter: false,
        linkedin: false,
        pinterest: false,
        cookie_solution_iubenda: true,
    };

    if (!RECAPTCHA_SITE_KEY) {
        console.error("App.tsx: RECAPTCHA_SITE_KEY non è definito!");
        return <div>Errore di configurazione: reCAPTCHA non disponibile.</div>;
    }

    const currentAppLanguage = i18nInstance.language.split('-')[0];
    const buttonPolicyId = getPolicyIdForLang(currentAppLanguage);

    // Se il sito è in manutenzione, mostra la pagina di manutenzione
    if (maintenance) {
        return (
            <div className="App">
                <div className="maintenance-wrapper">
                    <h1>Sito in costruzione</h1>
                    <p>Il sito è attualmente in stato di <strong>realizzazione</strong>.<br /> Torna presto a visitarci!</p>
                    <img src="assets/images/logo.png" alt="Logo" />
                </div>
            </div>
        );
    }

    return (
        <div className="App">
            <Header
                agencyData={headerAgencyData}
                languageSelector={
                    appConfig.ui.header.showLanguageSelector ? <LanguageSelector handleLanguageChange={handleLanguageChange} /> : <></>
                }
            />

            <Routes>

                <Route path={`${langPrefix}/`} element={
                    <RecaptchaWrapper>
                        <HomePage
                            seo={true}
                            vetrina2={appConfig.ui.homePage.vetrina2}
                            pageType={appConfig.ui.homePage.pageType}
                            filters={appConfig.ui.homePage.filters}
                            showLocationSearch={appConfig.ui.homePage.showLocationSearch}
                            showFooterBannerVendi={appConfig.ui.homePage.showFooterBannerVendi}
                            showPresentationSection={appConfig.ui.homePage.showPresentationSection}
                            showSocialSection={appConfig.ui.homePage.showSocialSection}
                            showVetrina1={appConfig.ui.homePage.showVetrina1}
                        />
                    </RecaptchaWrapper>
                }
                />

                <Route path={`${langPrefix}/immobili/*`} element={<Immobili />} />
                <Route path={`${langPrefix}/immobili`} element={<Immobili />} />

                <Route path={`${langPrefix}/immobile/:idSlug`} element={
                    <RecaptchaWrapper>
                        <PropertyDetail />
                    </RecaptchaWrapper>
                }
                />

                <Route path={`${langPrefix}/chi-siamo`} element={<AboutUs agencyId={AGENCY_ID} />} />

                <Route path={`${langPrefix}/servizi`} element={<Services />} />
                <Route path={`${langPrefix}/architetto-interno`} element={<InternalArchitect />} />
                <Route path={`${langPrefix}/promozione-immobile`} element={<PropertyPromotion />} />


                <Route path={`${langPrefix}/contatti`} element={
                    <RecaptchaWrapper>
                        <Contacts
                            contactForm={true}
                            agencyId={AGENCY_ID}
                            showAgents={showAgents}
                        />
                    </RecaptchaWrapper>
                }
                />

                <Route path={`${langPrefix}/dati`} element={<Dati agencyId={AGENCY_ID} />} />
                <Route path={`${langPrefix}/privacy`} element={<PolicyPageRoute policyType="privacy" />} />
                <Route path={`${langPrefix}/cookies`} element={<PolicyPageRoute policyType="cookies" />} />



                <Route path={`${langPrefix}/cerca`} element={
                    <RecaptchaWrapper>
                        <Buy />
                    </RecaptchaWrapper>
                } />
                <Route path={`${langPrefix}/vendi`} element={
                    <RecaptchaWrapper>
                        <Sell />
                    </RecaptchaWrapper>
                } />

                {appConfig.ui.routes.showBlogRoutes && (
                    <>
                        <Route path={`${langPrefix}/blog`} element={<Blog agencyId={AGENCY_ID} />} />
                        <Route path={`${langPrefix}/blog/:articleId`} element={<BlogDetail agencyId={AGENCY_ID} />} />
                    </>
                )}

                {appConfig.ui.routes.showSuccessRoutes && (
                    <>
                        <Route path={`${langPrefix}/immobili-successi`} element={<ImmobiliSuccess />} />
                        <Route path={`${langPrefix}/immobili-successi/:idSlug`} element={<PropertySold />} />
                    </>
                )}

                <Route path="*" element={<NotFound message="" />} />

            </Routes>

            <IubendaButton siteId={IUBENDA_SITE_ID} cookiePolicyId={buttonPolicyId} showBanner={appConfig.integrations.iubenda.showBanner} />

            <Footer />

            {appConfig.ui.showWhatsAppButton && <WhatsAppButton />}
            {appConfig.features.holidayPopup.enabled && <HolidayPopup />}

        </div>
    );
};

// --- Wrapper per I18n e Caricamento Iniziale ---
function AppWithI18n() {
    // RIMOSSA LA LOGICA DI CARICAMENTO PER I18N
    // const [i18nIsInitialized, setI18nIsInitialized] = useState(false);

    const { agency, loading: isLoadingAgency, error: agencyError } = useGetAgencyDetails(AGENCY_ID);

    useEffect(() => {
        if (agencyError) console.error("Errore caricamento dati agenzia:", agencyError);
    }, [agencyError]);

    const loaderAgencyData = { agency_name: agency?.name ?? DEFAULT_AGENCY_NAME};

    const [isLoaderActive, setIsLoaderActive] = useState(true);

    useEffect(() => {
        setTimeout(() => setIsLoaderActive(false), 3000);
    }, []);

    const isLoaderVisible = isLoadingAgency || isLoaderActive;

    return (
        <I18nextProvider i18n={i18n}>
            <Router>
                <ScrollToTop />
                {isLoaderVisible && (
                    <Loader agencyData={loaderAgencyData} isLoading={true} />
                )}
                <LanguageRouteHandler>
                    <App agencyData={agency} />
                </LanguageRouteHandler>
            </Router>
        </I18nextProvider>
    );
}

export default AppWithI18n;